import { formatDate } from "../../../../Utils/Utils";
import EnumsContext from "../../../../context/enums-context";
import ProductStatusCircle from "../../../UIElements/ProductStatusCircle";
import useApi from "../../../../Utils/BackendClient";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useGetEnums from "../../../../Utils/EnumsUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../../Utils/ResizeUtils";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";

import commonStyle from "../../CommonStyles.module.css";

const Divisions = () => {

    const isMobile = useIsMobile();
    const ctx = useContext(EnumsContext);
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef(null);

    const { changeDivisionStatus, deleteDivision, fetchDivisions } = useApi();

    const [divisions, setDivisions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reloadData, setReloadData] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    const [lazyState, setlazyState] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: null,
        sortOrder: null,
    });

    const enumTypes = useMemo(() => ["divisions"], []);

    useGetEnums(enumTypes);

    useEffect(() => {
        if(location.state !== null){
            toast.current.show(location.state.toast);
        }
        location.state = null;
    },[location.state, location]);

    useEffect(() => {

        const fetchData = async () => {

            const response = await fetchDivisions(lazyState);

            if (response.status !== 200){
                toast.current.show({ severity: "error", summary: "Помилка завантаження списку", detail: "Помилка сервера " + response.status, life: 3000 });
                setTotalRecords(0);
                setDivisions([]);
            }
            else{
                setTotalRecords(response.data.count);
                setDivisions(response.data.results);
            }
            
            setLoading(false);
        }

        fetchData();

    }, [reloadData, lazyState, fetchDivisions]);

    const ChangeDivisionStatusHandler = async (rowData) => {

        const action = rowData.is_active ? "deactivate" : "activate";
        const response = await changeDivisionStatus(rowData.id, action);

        if (response.status !== 200) {
            toast.current.show({ severity: "error", summary: "Помилка зміни статусу", detail: "Помилка сервера " + response.status, life: 3000 });
        }
        else {
            toast.current.show({ severity: 'success', summary: rowData.is_active ? 'Деактивація' : "Активація", detail: rowData.is_active ? 'Точку продажу деактивовано' : "Точку продажу активовано", life: 3000 });
            ctx.resetEnum("divisions");
        }

        setReloadData(!reloadData);
    }

    const DeleteButtonHandler = async (rowData) => {

        const response = await deleteDivision(rowData.id);

        if (response.status !== 204) {
            toast.current.show({ severity: "error", summary: "Помилка видалення", detail: "Помилка сервера " + response.status, life: 3000 });
        }
        else {
            toast.current.show({ severity: "success", summary: 'Видалення', detail: 'Точку продажу успішно видалено', life: 3000 });
        }

        setReloadData(!reloadData);
    };

    const CreateNewDivisionHandler = () => {
        navigate("/divisions/newdivision");
    }

    const EditButtonHandler = (rowData) => {

        const route = '/divisions/editdivision/' + rowData.id;
        navigate(route, { state: { id: rowData.id } });
    }

    const onPage = (event) => {
        event.page = event.page +1;
        setlazyState(event);
    };

    const confirmDelete = (event, rowData) => {
        confirmPopup({
            group: 'headless',
            target: event.currentTarget,
            message: 'Підтвердження видалення групи',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            acceptLabel: "Так",
            rejectLabel: "Hi",
            accept: () => {DeleteButtonHandler(rowData)}
        });
    }

    const changeStatus = (event, rowData) => {
        confirmPopup({
            group: 'headless',
            target: event.currentTarget,
            message: rowData.is_active ? 'Підтвердження деактивації групи' : 'Підтвердження активації групи',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            acceptLabel: "Так",
            rejectLabel: "Hi",
            accept: () => {ChangeDivisionStatusHandler(rowData)}
        });
    }

    const toolbarRightTemplate = () => {
        return (<div className="flex flex-wrap gap-2">
            <Button label="Додати точку продажу" severity="success" className={commonStyle.addButton} onClick={CreateNewDivisionHandler} />
        </div>)
    }

    const actionBodyTemplate = rowData => {
        return (<div className="flex flex-wrap gap-2">
            <Button
                icon={rowData.is_active ? "pi pi-times" : "pi pi-check"}
                className={`p-button-rounded ${rowData.is_active ? commonStyle.warningTag + " p-button-warning" : commonStyle.addButton + " p-button-success"}`}
                tooltip={rowData.is_active ? "Деактивувати" : "Активувати"}
                onClick={(event) => changeStatus(event, rowData)}
            />
            <Button
                icon="pi pi-trash"
                className={`p-button-rounded p-button-danger ${rowData.can_be_deleted ? commonStyle.deleteButton : commonStyle.closeButton}`}
                tooltip={rowData.can_be_deleted ? "Видалити" : "Неможливо видалити"}
                tooltipOptions={{ showOnDisabled: true }}
                disabled={!rowData.can_be_deleted}
                onClick={(event) => confirmDelete(event, rowData)}
            />
            <Button
                icon="pi pi-pencil"
                className={`p-button-rounded p-button-info ${commonStyle.editButton}`}
                onClick={() => EditButtonHandler(rowData)}
            />
        </div>
        );
    };

    const mobileRowBody = rowData => {
        return (
            <div className="flex justify-content-between flex-wrap">
                <div className="grid w-8">
                    <div className="col-2 flex justify-content-center align-items-center">
                        <ProductStatusCircle id={rowData.id} type="statusCircle" isActive={rowData.is_active}/>
                    </div>
                    <div className="col-10">
                        <div className="flex-column">
                            <div className="font-bold"><i className="pi pi-building"/> {rowData.name}</div>
                        </div>
                    </div>
                </div>
                <div className="flex justify-content-center align-items-center">
                    {actionBodyTemplate(rowData)}
                </div>
            </div>
        )
    }

    return (<>
        <Toast ref={toast} />   
        <Toolbar className="mb-4" end={toolbarRightTemplate} />
        <div className='grid'>
            <div className='col-12 justify-content-center'>
                {!isMobile && <DataTable value={divisions} stripedRows lazy rows={lazyState.rows} paginator onPage={onPage} first={lazyState.first} loading={loading} totalRecords={totalRecords} rowsPerPageOptions={[10, 20, 50]} tableStyle={{ minWidth: '60rem' }} >
                    <Column field="name" header="Точка продажу" sortable />
                    <Column field="created_by" header="Користувач" sortable/>
                    <Column field="created_at" header="Дата" sortable body={rowData => formatDate(rowData.created_at)}/>
                    <Column field="is_active" header="Статус" body={rowData => <ProductStatusCircle id={rowData.id} type="statusCircle" isActive={rowData.is_active}/>}/>
                    <Column field="action" body={actionBodyTemplate} />
                </DataTable>}
                {isMobile && <DataTable value={divisions} stripedRows lazy rows={lazyState.rows} paginator onPage={onPage} first={lazyState.first} loading={loading} totalRecords={totalRecords} rowsPerPageOptions={[10, 20, 50]} 
                    paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown" dataKey="id" emptyMessage="Клієнтів не знайдено" >
                    <Column field="id" headerStyle={{ display: 'none' }} body={mobileRowBody} />
                </DataTable>}
            </div>
            <ConfirmPopup group="headless" />
        </div>
    </>);
}

export default Divisions;